import {getResolvedSwitch} from './resolver';
import {buildSwitchRouteRow} from './reference';
import {highlightExistingAction} from '../../core/actionHighlighter';

// The dashboard header names its icons by action key and hides the readable
// text in a Tooltip that never reaches the element, so the aria-label is what
// there is to match on. The same shared header carries this button on the
// Home, Site, Switch, Client and Access Point dashboards.
const ACTION_LABELS = {
	download_report: ['download', 'Download Dashboard Report (PDF)'],
};

/**
 * Build the Switch Dashboard path for a navigation command.
 *
 * The route carries the switch in the path - `/switch-detail/:switchId` -
 * and that id is the serial number, which is also what a row click uses.
 * Every other destination in this bridge has a fixed route and says which
 * record it means in router state; this one cannot.
 *
 * @param {object} command navigation command
 * @return {string} router path
 */
export function buildSwitchDashboardPath(command) {
	const params = command.params || {};
	const serial = params.entity_id ?? params.serial_no;
	if (!serial) throw new Error('resolved_switch_not_available');
	return `/switch-detail/${encodeURIComponent(String(serial))}`;
}

/**
 * Build Canvas router state for the Switch Dashboard.
 *
 * The page reads the row it was handed rather than fetching it again, and
 * falls back to localStorage the way a row click leaves it there.
 *
 * @param {object} command navigation command
 * @return {object} router state
 */
export function buildSwitchNavigationState(command) {
	const params = command.params || {};
	const serial = params.entity_id ?? params.serial_no;
	// Falls back to what the command carries, because a switch taken from
	// the page the user is standing on was never looked up and so was never
	// retained. The route names it by serial either way, and the dashboard
	// fetches by that - the row here only saves it the round trip.
	const row = getResolvedSwitch(serial) || buildSwitchRouteRow(params);
	if (!row) throw new Error('resolved_switch_not_available');
	// Canvas's own row click drops the Status column before carrying the
	// row, because it holds a rendered element rather than a value.
	const carried = Object.fromEntries(
		Object.entries(row).filter(([key]) => key !== 'Status'),
	);
	try {
		window.localStorage.setItem('switchRow', JSON.stringify(carried));
	} catch (error) {
		// A browser refusing storage is not a reason to refuse the
		// navigation: the row travels in router state as well.
	}
	return carried;
}

/**
 * Verify that the requested Switch Dashboard mounted.
 * @param {object} command navigation command
 * @param {object} context registered page context
 * @return {boolean} readiness
 */
export function isSwitchNavigationReady(command, context) {
	const params = command.params || {};
	const requested = params.entity_id ?? params.serial_no;
	if (!context?.switch_id) return false;
	if (requested && String(context.switch_id) !== String(requested)) {
		return false;
	}
	const actionTarget = params.action_target;
	if (actionTarget && ACTION_LABELS[actionTarget]) {
		const shown = highlightExistingAction(
			ACTION_LABELS[actionTarget],
			[],
			{
				activate: params.action_activation === 'auto',
				commandId: command.command_id,
			},
		);
		// The Switch arrived; only its control is missing.
		return shown ? true : {ready: false, reason: 'action_control_unavailable'};
	}
	return true;
}
